import React, { Component } from 'react';
import { debounce, isEqual } from 'lodash';
import getConfig from 'next/config';

import get from 'lodash/get';
import Layout from '../Layout/index.tsx';
import ProgressBar from '../../components/UI/ProgressBar';
import KBAQuestions from '../KBAQuestions';
import {
  isUSTimezone,
  shouldValidatePlaceholderToken,
  storageAvailable
} from '../../helpers';
import { isNextRegisterStepPastVerification } from '../../state/utils';
import GroupMapper from '../../components/GroupMapper';
import css from './WidgetPage.module.scss';
import {
  ERR_ALREADY_REGISTERED,
  HTTP_ERROR_CODES,
  KYC_ERROR_CODES
} from './constants';
import { FEATURES, MINIMUM_WALLET_LOAD } from '../App/constants';
import WidgetWrapper from './WidgetWrapper.tsx';
import LegalAddress from '../Widgets/Address/LegalAddress';
import PlaidLoader from '../../components/UI/PlaidLoader/index.tsx';
import datadogRumClient from '../../utils/datadog-rum';
import { REASONS, useReauthManagement } from '../../hooks/useReauthManagement';
import registrationShortcut from '../../helpers/modularReduxRegistrationShortcut.ts';
import Loader from '../../components/UI/Loader/index.tsx';
import { setStandardToggle } from '../../helpers/toggles';
import { SmsSubscriptionType } from '../../types/braze.types';
import { planPrices } from '../../utils/prices';

const { publicRuntimeConfig } = getConfig();
const environment = get(publicRuntimeConfig, 'nodeEnv');

const {
  showFundingAccountBackButton,
  showProgressBarInFlow,
  getConfirmIdGroup,
  getFundWalletGroup,
  getNoProgressGroups
} = GroupMapper;

class WidgetPage extends Component {
  initialize = debounce(
    (
      regAccessToken,
      tempPhoneNumber,
      role,
      preregisteredUserId,
      authToken,
      dob,
      partnerSource,
      registrationId,
      preRegPlanSelection,
      isFcc
    ) => {
      this.props.initialize({
        accessToken: regAccessToken,
        uniqueId: tempPhoneNumber,
        role,
        preregisteredUserId,
        authToken,
        partnerSource,
        registrationId,
        dob,
        plan: preRegPlanSelection,
        isFcc
      });
    },
    200
  );

  constructor() {
    super();

    this.state = {
      questions: [],
      questionsOverride: false,
      questionsReason: '',
      errors: false,
      displayKYC: false,
      displayLegalAddress: false,
      displayConfirmId: false,
      displayFundWallet: false,
      ssnLast4: undefined,
      confirmIdAnswers: undefined,
      confirmIdQuestions: undefined,
      activeErrorId: undefined,
      activeErrorFlowId: undefined,
      activeErrorGroupId: undefined
    };
  }

  componentWillMount() {
    let { regAccessToken } = this.props.app;
    let { tempPhoneNumber, role, preregisteredUser } = this.props.user;
    let userCompleted = false;
    let registrationToken = '';

    let authToken;

    if (this.props.user.registeredUser) {
      const { token } = this.props.user.registeredUser;
      authToken = token; // assignment here so linter doesn't complain.
      preregisteredUser = this.props.user.registeredUser.user;
    }

    if (this.props.app.inAppPreReg && this.props.user.registeredUser) {
      preregisteredUser = this.props.user.registeredUser;
    }

    if (storageAvailable('sessionStorage')) {
      if (regAccessToken && tempPhoneNumber && role) {
        sessionStorage.setItem('regAccessToken', regAccessToken);
        sessionStorage.setItem('tempPhoneNumber', tempPhoneNumber);
        sessionStorage.setItem('role', role);
      } else {
        regAccessToken = sessionStorage.getItem('regAccessToken');
        tempPhoneNumber = sessionStorage.getItem('tempPhoneNumber');
        role = sessionStorage.getItem('role');
        userCompleted = sessionStorage.getItem('userCompleted');
        registrationToken = sessionStorage.getItem('registrationToken');
      }
      if (authToken) {
        sessionStorage.setItem('authToken', authToken);
      } else {
        authToken = sessionStorage.getItem('authToken');
      }
      if (preregisteredUser) {
        sessionStorage.setItem(
          'preregisteredUser',
          JSON.stringify(preregisteredUser)
        );
      }
    }
    if (
      shouldValidatePlaceholderToken(
        preregisteredUser,
        this.props.toggles.requireTokenForApproversEnabled
      )
    ) {
      if (!this.props.app.placeholderToken) {
        // Approver didn't use an invite link so bounce them
        this.props.setNextRegStep('approver_declined');
        return;
      }
      this.props.validatePlaceholderToken(
        this.props.app.placeholderToken,
        preregisteredUser.id,
        regAccessToken
      );
    }
    if (userCompleted && registrationToken) {
      this.props.getAnswers(registrationToken);
      this.props.getUser(registrationToken);
      this.props.getFundingAccounts(registrationToken);
      this.setState({
        questions: ['complete']
      });
    } else if (regAccessToken && regAccessToken !== 'undefined') {
      this.props.analytics.trackEvent('Registration Entered', {
        eventProps: {
          role,
          source: this.props.app.appOs ? this.props.app.appOs : 'web',
          returning_user: !!preregisteredUser
        }
      });

      const partnerSource = this.props.user.partner || null;
      // eslint-disable-next-line no-shadow
      const registrationId = this.props.app.registrationId || null;

      const dob = role === 'child' ? this.props.user.dob : undefined;
      this.initialize(
        regAccessToken,
        tempPhoneNumber,
        role,
        preregisteredUser ? preregisteredUser.id : null,
        authToken,
        dob,
        partnerSource,
        registrationId,
        this.props.user.preRegPlanSelection,
        this.props.app.showFccPreReg
      );
    } else {
      this.props.history.replace('/');
    }
  }

  componentDidMount() {
    if (this.props.app.shouldRedirectToExternalBrowser) {
      this.props.failedWriaUrlValidation();
    }
    this.firePreregisteredMessaging();
    window.scrollTo(0, 0);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.app.unresolvedErrors !== nextProps.app.unresolvedErrors) {
      // This resolves to true every time you submit an answer whether unresolvedErrors
      // actually changes or not due comparing arrays, proceed with caution!
      const errorsExist =
        nextProps.app.unresolvedErrors &&
        nextProps.app.unresolvedErrors.length > 0;
      const errorsDoNotExist =
        !nextProps.app.unresolvedErrors ||
        nextProps.app.unresolvedErrors.length < 1;
      const errorsDidExist =
        this.props.app.unresolvedErrors &&
        this.props.app.unresolvedErrors.length > 0;

      const processingOnInitialize =
        this.props.app.initializeLoading && !nextProps.app.initializeLoading;

      if (errorsExist) {
        const hasPassed = nextProps.app.unresolvedErrors.some(
          err => err.code === KYC_ERROR_CODES.pass
        );
        // Deal with the first one. Once resolved, props will update and we'll address the next one.
        const unresolved = nextProps.app.unresolvedErrors[0];

        this.processUnresolvedError(
          unresolved,
          nextProps.app.resetKBAFlow,
          hasPassed,
          processingOnInitialize
        );
      } else if (errorsDoNotExist) {
        this.setState({
          activeErrorId: undefined,
          activeErrorFlowId: undefined,
          activeErrorGroupId: undefined
        });
        this.moveToNextQuestions(nextProps);

        // If user has complete as next step and resolved existing unresolvedErrors, call /complete
        if (errorsDidExist && nextProps.app.completeUser) {
          this.props.complete(nextProps.app.registrationToken);
        }
      }
    }

    // If receiving a flowId, send it to FullStory
    if (
      !isEqual(nextProps.app.flowId, this.props.app.flowId) &&
      window.FS &&
      typeof window.FS.event === 'function'
    ) {
      window.FS.event('Flow Decided', {
        flow_id: nextProps.app.flowId
      });

      datadogRumClient.addRumGlobalContext('flowId', nextProps.app.flowId);
    }
    if (
      !isEqual(
        nextProps.app.registrationToken,
        this.props.app.registrationToken
      )
    ) {
      // If receiving a new registrationToken, get the completed answers
      this.props.getAnswers(nextProps.app.registrationToken);

      // Registration Shortcut for testing in lower environments
      if (
        environment !== 'production' &&
        nextProps.app.shortcut &&
        nextProps.app.groupId === 'group_1'
      ) {
        registrationShortcut(
          nextProps.user.tempPhoneNumber,
          nextProps.app.registrationToken,
          nextProps.app.shortcut,
          nextProps.app.flowId,
          nextProps.app.shortcutPlan,
          this.props.answer
        );
      }
    }
    if (
      nextProps.user.registeredUser?.user?.id &&
      !this.props.user.registeredUser?.user?.id &&
      (this.props.featureToggles.kidFirstRegParentInviteV0.enabled ||
        this.props.featureToggles.kidFirstRegParentInviteV1.enabled)
    ) {
      this.props.identifyUser({
        userAlias: {
          alias_name: 'segment_anonymous_id',
          alias_label: this.props.anonymousId
        },
        externalId: nextProps.user.registeredUser.user.id
      });
    }
    // If user has received Complete as next step, call /complete if no errors remain
    if (
      !isEqual(nextProps.app.completeUser, this.props.app.completeUser) &&
      nextProps.app.completeUser &&
      (nextProps.app.unresolvedErrors || []).length === 0
    ) {
      this.props.complete(nextProps.app.registrationToken);
    }
    // If user answers set_initial_load_amount, then get answers again
    if (
      !isEqual(nextProps.app.answerLoading, this.props.app.answerLoading) &&
      this.state.questions.indexOf('set_funding_account') !== -1
    ) {
      this.props.getAnswers(nextProps.app.registrationToken);
    }

    // If displayLegalAddress, then display legal address component
    if (
      !isEqual(
        nextProps.app.displayLegalAddress,
        this.props.app.displayLegalAddress
      )
    ) {
      this.setState({
        displayLegalAddress: nextProps.app.displayLegalAddress
      });
    }

    // If user completes registration, then get their current pricing plan
    if (
      nextProps.user.shouldGetPricingPlanAfterComplete !==
        this.props.user.shouldGetPricingPlanAfterComplete &&
      nextProps.user.shouldGetPricingPlanAfterComplete &&
      this.isParent(nextProps) &&
      this.props.app.stargatewayKey
    ) {
      this.props.getPricingPlan(this.props.app.stargatewayKey);
    }
  }

  showWidgetWrapper() {
    return (
      !this.state.displayKYC &&
      !this.state.displayLegalAddress &&
      !this.props.app.submitPlaidLoading &&
      this.state.questions.length &&
      !!this.props.app.answers
    );
  }

  isParent(nextProps) {
    return (
      (nextProps.app.flowId && nextProps.app.flowId.includes('parent')) ||
      (nextProps.app.answers && nextProps.app.answers.setRole === 'parent')
    );
  }

  /**
   *
   * @param {{id: ID, code: String, action: String, field: String, questions: [String], message: String, meta: Object}} unresolvedError the error we need to process
   */
  processUnresolvedError(
    unresolvedError,
    shouldResetKBA,
    hasPassedKBA,
    processingOnInitialize
  ) {
    const { getReauthAttemptsByReason } = useReauthManagement();

    // Need to force WidgetWrapper to refresh completely when the questions to handle the error
    // are the same as the ones on the screen
    switch (unresolvedError.code) {
      case KYC_ERROR_CODES.pass:
        return this.props.resolveError({ errorId: unresolvedError.id });
      case KYC_ERROR_CODES.fail:
      case KYC_ERROR_CODES.timeout:
        if (
          this.props.toggles.kycRetryEnabled.enabled &&
          this.props.app.isKycRetryAttempt
        ) {
          this.props.setIsKycRetryAttempt({ isKycRetryAttempt: false });
          this.props.clearError();
          this.setState({
            displayKYC: false,
            displayConfirmId: false,
            displayLegalAddress: false,
            questions: unresolvedError.questions,
            activeErrorId: unresolvedError.id,
            activeErrorFlowId: unresolvedError.flowId,
            activeErrorGroupId: unresolvedError.groupId
          });
          return;
        }
        return this.redirectToVerifyDeclined();

      case KYC_ERROR_CODES.partial:
      case KYC_ERROR_CODES.challenge:
        // In the case of partial failure, we need to display KBA questions
        // If we're originating the flow (i.e we enter through initialize, force the user to re-answer everything)
        if (shouldResetKBA) {
          this.props.completeResetKBAFlow();
          this.setState({
            displayKYC: hasPassedKBA,
            activeErrorId: unresolvedError.id,
            activeErrorFlowId: unresolvedError.flowId,
            activeErrorGroupId: unresolvedError.groupId
          });
          if (!hasPassedKBA) {
            return this.resetToConfirmId();
          }
        }
        this.setState({
          displayKYC: true,
          displayConfirmId: false,
          displayLegalAddress: false,
          questions: unresolvedError.questions,
          activeErrorId: unresolvedError.id,
          activeErrorFlowId: unresolvedError.flowId,
          activeErrorGroupId: unresolvedError.groupId
        });
        return;
      case HTTP_ERROR_CODES.unauthorized:
        if (processingOnInitialize) {
          this.setQuestionsAndActiveError(unresolvedError);
          return;
        }
        this.createError(unresolvedError);
        return;
      case HTTP_ERROR_CODES.invalidIPAddress:
        if (getReauthAttemptsByReason(REASONS.INVALID_IP) > 0) {
          return;
        }
        this.props.registrationReauth(REASONS.INVALID_IP);
        return;
      case ERR_ALREADY_REGISTERED:
        // If we're originating the flow (i.e we enter through initialize), don't create an error
        if (!processingOnInitialize) {
          this.createError(unresolvedError);
        }
        this.setQuestionsAndActiveError(unresolvedError);
        return;
      default:
        this.createError(unresolvedError);
        if (unresolvedError.questions.length > 0) {
          this.setQuestionsAndActiveError(unresolvedError);
        } else {
          // TODO @steph: Figure out a better solution for this once registration is fully refactored
          // For now, if the error doesn't come with questions and doesn't otherwise have a solution, then it's not something that
          // we need to block registration for. So mark it resolved
          this.props.resolveError({ errorId: unresolvedError.id });
        }
    }
  }

  setQuestionsAndActiveError = unresolvedError => {
    this.setState({
      questions: unresolvedError.questions,
      activeErrorId: unresolvedError.id,
      activeErrorFlowId: unresolvedError.flowId,
      activeErrorGroupId: unresolvedError.groupId
    });
  };

  createError = unresolvedError => {
    this.props.createError({
      message: unresolvedError.message,
      registrationStep: unresolvedError.questions.toString(),
      error: true
    });
  };

  redirectToVerifyDeclined = () =>
    this.props.setNextRegStep('parent_verify_declined');

  firePreregisteredMessaging() {
    if (this.handleShouldOverrideQuestions([]).questions.length > 0) {
      return;
    }

    const toggles = get(this, 'props.featureToggles');
    const plans = planPrices(toggles);
    if (this.props.user.isChildInvitedByParent) {
      this.props.createError({
        message:
          'Cool, it looks like your mom or dad added you to their account.',
        registrationStep: 'widget_page',
        error: false
      });
    } else if (this.props.user.isParentInvitedByChild) {
      this.props.createError({
        message: `Great! It looks like your child pre-registered your account. After your 1 month free trial, Greenlight is $${plans.standard}/month including debit cards for up to 5 kids.`,
        registrationStep: 'widget_page',
        error: false
      });
    } else if (this.props.user.preregisteredUser) {
      this.props.createError({
        message: "Great! It looks like you've been invited to join an account.",
        registrationStep: 'widget_page',
        error: false
      });
    }
  }

  moveToNextQuestions = nextProps => {
    window.scrollTo(0, 0);
    this.setState({
      questions: nextProps.app.questions,
      questionsOverride: false,
      displayKYC: false,
      displayConfirmId: false,
      displayLegalAddress: false,
      displayFundWallet: false
    });

    let token = '';
    if (nextProps.app.registrationToken) {
      token = nextProps.app.registrationToken;
    }

    if (token) {
      this.props.getAnswers(token);
      const url = `/reg/${nextProps.app.flowId}/${nextProps.app.groupId}`;
      this.props.history.push(url);
    } else {
      this.props.analytics.trackEvent('Registration Error Triggered', {
        eventProps: {
          error_message: 'Token missing on moving to next questions',
          appOs: this.props.app.appOs,
          appVersion: this.props.app.appVersion,
          flowId: nextProps.app.flowId,
          groupId: nextProps.app.groupId
        }
      });
      this.props.history.replace('/');
    }

    this.props.segment.page('Widget Page', 'Registration', {
      group_id: nextProps.app.groupId,
      flow_id: nextProps.app.flowId
    });
  };

  widgetClicked = question => {
    this.props.analytics.trackEvent('Registration Widget Clicked', {
      eventProps: {
        widget_name: question,
        group_id: this.props.app.groupId,
        flow_id: this.props.app.flowId
      }
    });
  };

  handleSMSMarketingSubscription = prevProps => {
    const appState = this.props.app;
    const toggleState = this.props.toggles;
    const registeredUser = get(this.props.user, 'registeredUser.user');
    const prevUser = get(prevProps.user, 'registeredUser.user');
    const isOptedIn = appState.smsNotificationsOptedStatus === 'opted_in';

    if (
      !registeredUser ||
      registeredUser === prevUser ||
      !toggleState.smsNotificationsEnabled ||
      !isOptedIn
    )
      return;

    this.props.subscribeSMS({
      userId: registeredUser.id,
      subscriptionType: SmsSubscriptionType.MARKETING
    });
  };

  questionsDoNotInclude(question) {
    const questions = get(this, 'props.app.questions') || [];
    return questions.indexOf(question) === -1;
  }

  handleShouldOverrideQuestions(questions, answers) {
    const fts = get(this, 'props.featureToggles') || {
      enableCorePriceChange: { enabled: false },
      riskFreePaintedDoor: { enabled: false },
    };

    const setPlan = get(answers, 'setPlan') || {
      pricingPlanName: '',
      basePrice: -1,
      riskFreeEnabled: false
    };
    if (!fts.enableCorePriceChange.enabled && !fts.riskFreePaintedDoor.enabled) return { questions };
    
    if (
      this.questionsDoNotInclude('complete') &&
      (this.props.app.flowId && this.props.app.flowId.includes('parent')) &&
      !this.props.user.partner &&
      (setPlan.pricingPlanName !== '' && !setPlan.riskFreeEnabled && fts.riskFreePaintedDoor.enabled )
    ) {
      return { questions: ['set_plan'], reason: 'RISK_FREE'};
    }

    const prices = planPrices(fts);

    if (
      setPlan.pricingPlanName === 'standard' &&
      this.questionsDoNotInclude('complete') &&
      (!setPlan || setPlan.basePrice !== prices.standard.toString())
    ) {
      return { questions: ['set_plan'], reason: 'CORE_PRICE_CHANGE'} // with updated prices, we should ask returning user to re select plan
    }

    return { questions };
  }

  componentDidUpdate(prevProps) {
    const plan = app => get(app, 'answers.setPlan.pricingPlanName');
    const planChanged = plan(this.props.app) !== plan(prevProps.app);

    if (
      this.props.toggles.preventNonUSTimezones.enabled &&
      !isUSTimezone() &&
      isNextRegisterStepPastVerification(this.props.user.nextRegStep)
    ) {
      this.props.analytics.trackEvent('Non-US Timezone Declined', {
        eventProps: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      });
      this.props.history.replace('/verify-hard-declined');
    }

    if (
      this.props.user.preregisteredUser !== prevProps.user.preregisteredUser
    ) {
      this.firePreregisteredMessaging();
    }
    if (this.props.toggles.shouldBounceInvitedUsersWhoDidntUseInviteLink) {
      this.props.setNextRegStep('approver_declined');
    }

    if (plan(this.props.app) && planChanged) {
      const anonymousId = this.props.analytics.getAnonymousId();
      setStandardToggle({
        anonymousId,
        attributes: {
          planName: plan(this.props.app)
        },
        featureName: FEATURES.minimumWalletLoad,
        cookieName: 'minimumWalletLoadVariation',
        segmentPropertyName: 'minimumWalletLoadVariation',
        onVariationCallback: (variationKey, variables, enabled) => {
          if (enabled) {
            this.props.setMinimumWalletLoad({
              shouldShift:
                variationKey !== MINIMUM_WALLET_LOAD.experimentOffVariant,
              minimumLoadAmount:
                variables[MINIMUM_WALLET_LOAD.minimumAmountKey] ||
                MINIMUM_WALLET_LOAD.defaultMinimumAmount,
              displayMinimumAmount:
                variables[MINIMUM_WALLET_LOAD.displayMinimumAmountKey]
            });
          }
        }
      });
    }

    this.handleSMSMarketingSubscription(prevProps);

    if (prevProps.app.answers && !this.state.questionsOverride) {
      const overrideQuestions = this.handleShouldOverrideQuestions(
        prevProps.app.questions,
        prevProps.app.answers
      );
      if (
        overrideQuestions.questions !== prevProps.app.questions &&
        overrideQuestions.questions !== this.state.questions
      ) {
        this.setState({
          questions: overrideQuestions.questions,
          questionsOverride: true,
          questionsOverrideReason: overrideQuestions.reason
        });
      }
    } else if (this.state.questionsOverride) {
      if (
        prevProps.app.answers &&
        this.props.app.answers &&
        !isEqual(this.props.app.answers, prevProps.app.answers)
      ) {
        this.setState({
          questionsOverride: false,
          questions: prevProps.app.questions
        });
      }
    }
  }

  submitAnswers = (questions, answers) => {
    // If in a lower environment and someone answers ParentName with shortcut, then shortcut instead of answering like normal
    if (environment !== 'production') {
      // Check for not being in production before anything else
      if (
        questions.indexOf('set_parental_unit_name') !== -1 &&
        answers[questions.indexOf('set_parental_unit_name')].indexOf(
          'shortcut='
        ) !== -1 &&
        this.props.app.groupId === 'group_1'
      ) {
        const parentNameAnswer =
          answers[questions.indexOf('set_parental_unit_name')];

        registrationShortcut(
          this.props.user.tempPhoneNumber,
          this.props.app.registrationToken,
          this.props.app.shortcut,
          this.props.app.flowId,
          this.props.app.shortcutPlan,
          this.props.answer,
          parentNameAnswer
        );
        return;
      }
    }

    if (
      this.props.toggles.uspsValidationUnblock.enabled &&
      questions.indexOf('set_legal_address') !== -1
    ) {
      answers[
        questions.indexOf('set_legal_address')
      ].bypassUspsValidation = true;
      if (questions.indexOf('set_shipping_address') !== -1) {
        answers[
          questions.indexOf('set_shipping_address')
        ].bypassUspsValidation = true;
      }
    }

    this.props.analytics.trackEvent('Registration Widget Submitted', {
      eventProps: {
        widgets_submitted: questions,
        group_id: this.props.app.groupId,
        flow_id: this.props.app.flowId
      }
    });

    const hasOpenError =
      this.state.activeErrorId &&
      this.props.app.unresolvedErrors &&
      this.props.app.unresolvedErrors.length > 0;

    const err = hasOpenError
      ? this.props.app.unresolvedErrors.find(
          e => e.id === this.state.activeErrorId
        ) || this.props.app.unresolvedErrors[0]
      : null;

    this.props.answer({
      accessToken: this.props.app.registrationToken,
      questions,
      answers,
      // Pass this to resolve the error when we're done answering to ensure that
      // unresolvedErrors gets populated before the screen navigates. If null, nothing happens
      resolveErrorId: this.state.activeErrorId
    });

    if (this.state.displayFundWallet) {
      this.setState({
        displayFundWallet: false
      });
    }

    if (err) {
      this.props.analytics.trackEvent('Registration Widget Corrected', {
        eventProps: {
          widgets_submitted: questions,
          group_id: this.props.app.groupId,
          flow_id: this.props.app.flowId,
          error_code: err.code,
          error_action: err.action
        }
      });
    }
  };

  resetToFundWallet = () => {
    // Need to remove this function once back button functionality is added for other pages
    const fundWalletQuestions = ['set_initial_funding_amount'];

    if (this.props.app.flowId === 'parent_flow_9') {
      fundWalletQuestions.push('set_autofunding_preference');
    }

    this.props.clearError();

    this.setState({
      questions: fundWalletQuestions,
      activeErrorId: null,
      displayFundWallet: true
    });
  };

  resetToConfirmId = () => {
    this.setState({
      questions: [
        'set_legal_first_name',
        'set_legal_last_name',
        'set_legal_address',
        'set_user_date_of_birth',
        'set_user_ssn_last4',
        'accept_terms'
      ],
      displayConfirmId: true
    });
    this.props.displayLegalAddress(false);
  };

  render() {
    const flowId = this.state.activeErrorFlowId || this.props.app.flowId;
    let groupId = this.state.activeErrorGroupId || this.props.app.groupId;
    const noProgressGroups = getNoProgressGroups(flowId);

    if (this.state.displayConfirmId) {
      groupId = getConfirmIdGroup(flowId);
    }
    if (this.state.displayFundWallet) {
      groupId = getFundWalletGroup(flowId);
    }

    let totalGroups = this.props.app.totalGroupsInFlow;
    let currentGroup = this.props.app.groupId
      ? parseFloat(this.props.app.groupId.replace(/\D/g, ''), 10)
      : '';

    if (noProgressGroups) {
      totalGroups -= noProgressGroups.length;
      const ogCurrent = currentGroup;

      for (let i = 0; i < noProgressGroups.length; i += 1) {
        if (ogCurrent > noProgressGroups[i]) {
          currentGroup -= 1;
        }
      }
    }

    const showProgressBar =
      this.state.questions.indexOf('complete') === -1 &&
      showProgressBarInFlow(flowId) &&
      !(
        this.props.toggles.defaultMax.enabled ||
        this.props.toggles.defaultInfinity.enabled
      );

    if (
      !this.props.app.answers ||
      this.props.app.initializeLoading ||
      this.props.app.isKycRetryAttempt
    ) {
      return <Loader />;
    }

    return (
      <div tabIndex="1">
        {/* This back button is not a good solution, will need to create back functionality in registration service then remove this and displayFundWallet */}
        {showFundingAccountBackButton(flowId) &&
          get(this.props.app, 'answers.setInitialFundingAmount') &&
          this.state.questions.indexOf('set_funding_account') !== -1 && (
            <button
              className={css.backButton}
              onClick={this.resetToFundWallet}
              data-e2e="back-button"
              data-testid="back-button"
            >
              <img
                className={css.backDesktop}
                src="/static/back-arrow.svg"
                alt="Back Button"
              />
              <img
                className={css.backMobile}
                src="/static/back-arrow-mobile.svg"
                alt="Back Button"
              />
            </button>
          )}
        <Layout data-e2e={`${flowId}-${groupId}`}>
          {showProgressBar && (
            <ProgressBar total={totalGroups} current={currentGroup} />
          )}
          {this.props.app.submitPlaidLoading && <PlaidLoader />}
          {this.state.displayKYC && (
            <KBAQuestions ssn={this.state.ssnLast4} {...this.props} />
          )}

          {this.state.displayLegalAddress && (
            <LegalAddress
              answers={this.state.confirmIdAnswers}
              questions={this.state.questions}
              resetToConfirmId={this.resetToConfirmId}
              onSubmit={this.submitAnswers}
              {...this.props}
            />
          )}
          {this.showWidgetWrapper() && (
            <WidgetWrapper
              {...this.props}
              key={`${groupId}_${this.state.activeErrorId}`}
              overriddenGroupId={groupId}
              questions={this.state.questions}
              questionsOverride={this.state.questionsOverride}
              resolvesErrorId={this.state.activeErrorId}
              onSubmit={this.submitAnswers}
              displayLegalAddress={this.props.displayLegalAddress}
              onClick={this.widgetClicked}
              onSetSsn={ssnLast4 => this.setState({ ssnLast4 })}
              onSetConfirmIdAnswers={confirmIdAnswers =>
                this.setState({ confirmIdAnswers })
              }
              onSetConfirmIdQuestions={confirmIdQuestions =>
                this.setState({ confirmIdQuestions })
              }
              ssn={this.state.ssnLast4}
            />
          )}
        </Layout>
      </div>
    );
  }
}

export default React.memo(WidgetPage);
